/**
 * The SMS length rules the Outreach tab's Text card counts against (L5).
 *
 * A text goes out in one of two encodings, and the carrier — not Phoenix —
 * decides which, from the characters in it:
 *
 *   GSM-7   Every character is in the GSM 03.38 default alphabet or its
 *           extension table. 160 characters fit one segment; a longer message
 *           is split into segments of 153 (seven go to the concatenation
 *           header). The nine extension characters `^ { } \ [ ~ ] | €` are sent
 *           as an escape plus the character, so each counts as TWO, and an
 *           encoder never splits that pair across a segment boundary.
 *
 *   UCS-2   One character outside both tables — a curly quote pasted from a
 *           word processor is the usual one — and the WHOLE message switches to
 *           Unicode: 70 per segment, 67 per segment once split. A character
 *           outside the Basic Multilingual Plane (an emoji) is two UTF-16 units
 *           and counts as two.
 *
 * Nothing here sends anything. The count exists so a specialist can see, before
 * copying, that one stray character has tripled what their text costs to send.
 */

/** The GSM 03.38 default alphabet (the escape code 0x1B is not a character). */
const GSM7_BASIC = new Set(
  Array.from(
    "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?" +
      "¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà",
  ),
);

/** The extension table — sent as ESC + character, so two septets each. */
const GSM7_EXTENSION = new Set(Array.from("^{}\\[~]|€"));

export type SmsEncoding = "GSM-7" | "UCS-2";

export interface SmsCount {
  encoding: SmsEncoding;
  /** Septets under GSM-7, UTF-16 units under UCS-2 — what the carrier bills. */
  characters: number;
  /** 0 for an empty text. */
  segments: number;
  /** Capacity of a lone segment: 160 (GSM-7) or 70 (UCS-2). */
  singleLimit: number;
  /** Capacity of each segment once split: 153 (GSM-7) or 67 (UCS-2). */
  multiLimit: number;
  /** The characters that forced UCS-2, each once, in order of appearance. */
  nonGsmCharacters: string[];
}

const GSM7_SINGLE = 160;
const GSM7_MULTI = 153;
const UCS2_SINGLE = 70;
const UCS2_MULTI = 67;

/** Is this one code point in the default alphabet or the extension table? */
export function isGsm7Character(ch: string): boolean {
  return GSM7_BASIC.has(ch) || GSM7_EXTENSION.has(ch);
}

/**
 * Counts one segment at a time, so a two-unit character that would straddle a
 * boundary opens the next segment instead — an escape pair (GSM-7) or a
 * surrogate pair (UCS-2) is never split.
 */
function countSegments(widths: number[], total: number, single: number, multi: number): number {
  if (total === 0) return 0;
  if (total <= single) return 1;
  let segments = 1;
  let used = 0;
  for (const w of widths) {
    if (used + w > multi) {
      segments += 1;
      used = 0;
    }
    used += w;
  }
  return segments;
}

export function countSms(text: string): SmsCount {
  const codePoints = Array.from(text);

  const nonGsm: string[] = [];
  for (const ch of codePoints) {
    if (!isGsm7Character(ch) && !nonGsm.includes(ch)) nonGsm.push(ch);
  }

  if (nonGsm.length === 0) {
    const widths = codePoints.map((ch) => (GSM7_EXTENSION.has(ch) ? 2 : 1));
    const characters = widths.reduce((a, b) => a + b, 0);
    return {
      encoding: "GSM-7",
      characters,
      segments: countSegments(widths, characters, GSM7_SINGLE, GSM7_MULTI),
      singleLimit: GSM7_SINGLE,
      multiLimit: GSM7_MULTI,
      nonGsmCharacters: [],
    };
  }

  // A code point above U+FFFF is a surrogate pair — two UTF-16 units.
  const widths = codePoints.map((ch) => ((ch.codePointAt(0) ?? 0) > 0xffff ? 2 : 1));
  const characters = widths.reduce((a, b) => a + b, 0);
  return {
    encoding: "UCS-2",
    characters,
    segments: countSegments(widths, characters, UCS2_SINGLE, UCS2_MULTI),
    singleLimit: UCS2_SINGLE,
    multiLimit: UCS2_MULTI,
    nonGsmCharacters: nonGsm,
  };
}

/**
 * How to NAME a character in the warning. A printable glyph is shown as itself;
 * anything that would render as nothing or as a space — a non-breaking space, a
 * zero-width joiner, a tab — is named by its code point, because "Contains “ ”"
 * tells a reader nothing.
 */
export function describeCharacter(ch: string): string {
  const cp = ch.codePointAt(0) ?? 0;
  const invisible = /\s/u.test(ch) || /\p{C}/u.test(ch) || cp === 0x200b || cp === 0x200d;
  const code = `U+${cp.toString(16).toUpperCase().padStart(4, "0")}`;
  return invisible ? code : `“${ch}”`;
}
